import React, { useMemo, useState } from "react";
import usePromoCodes from "../../../hooks/promoCode/usePromoCodes";
import { User } from "../../shared/users/UserDetails";
import {
  X,
  Gift,
  Users,
  TrendingUp,
  Clock,
  CheckCircle,
  Edit2,
  UserPlus,
  Calendar,
  Save,
  Loader2,
  ArrowUpRight,
  Power,
} from "lucide-react";
import UpdateRewardModal from "./UpdateRewardModal";
import UpdatePromoCodeModal from "./UpdatePromoCodeModal";
import { alertError, alertSuccess } from "../../../helpers/alertError";
import { ReferralDetail } from "../../../services/codepromoService";
import useCountries from "../../../hooks/countries/useCountries";
import { resolveCurrencyFromCountryId } from "../../../helpers/currency";

interface PromoCodeStatsModalProps {
  user: User;
  onClose: () => void;
}

export default function PromoCodeStatsModal({ user, onClose }: PromoCodeStatsModalProps) {
  const [showUpdateRewardModal, setShowUpdateRewardModal] = useState(false);
  const [showUpdateCodeModal, setShowUpdateCodeModal] = useState(false);
  const [editingShareMessage, setEditingShareMessage] = useState(false);
  const [shareMessageDraft, setShareMessageDraft] = useState("");
  const [savingShareMessage, setSavingShareMessage] = useState(false);
  const [savingStatus, setSavingStatus] = useState(false);
  const {
    promoCodeStat,
    promoCode,
    referrals,
    loading,
    error,
    updateRewardPerInvite,
    refresh,
    updatePromoCode,
    updateShareMessage,
    updatePromoStatus,
  } = usePromoCodes({
    userId: user.id,
  });
  const { countries } = useCountries({});

  const displayCurrency =
    promoCodeStat?.currency ||
    promoCode?.currency ||
    user.transaction_stats?.total_sent_currency ||
    resolveCurrencyFromCountryId(countries, user.country_id);

  const getReferralEarningsCurrency = (referral: ReferralDetail) =>
    referral.earnings_currency || promoCodeStat?.currency || displayCurrency;

  const getReferralTransferCurrency = (referral: ReferralDetail) => referral.transfer_currency || "CAD";

  const referralList: ReferralDetail[] = useMemo(() => {
    const raw: ReferralDetail[] = promoCodeStat?.referrals?.length ? promoCodeStat.referrals : referrals || [];

    return raw.map((referral) => ({
      ...referral,
      earnings_currency: referral.earnings_currency || promoCodeStat?.currency || displayCurrency,
      transfer_currency: referral.transfer_currency || "CAD",
    }));
  }, [promoCodeStat, referrals, displayCurrency]);

  const handleUpdatePromoCode = async (newCode: string) => {
    try {
      await updatePromoCode(newCode);
      await refresh();
    } catch (error: any) {
      alertError(error.message || "Erreur lors de la modification du code promo");
      throw error;
    }
  };

  const handleSaveShareMessage = async () => {
    setSavingShareMessage(true);
    try {
      await updateShareMessage(shareMessageDraft);
      setEditingShareMessage(false);
      alertSuccess("Message de partage mis à jour avec succès");
    } catch (error) {
      alertError("Erreur lors de la mise à jour du message de partage");
    } finally {
      setSavingShareMessage(false);
    }
  };

  const currentPromoStatus = promoCode?.status || promoCodeStat?.status || "ACTIVE";
  const isPromoActive = currentPromoStatus === "ACTIVE";

  const handleTogglePromoStatus = async () => {
    const nextStatus = isPromoActive ? "INACTIVE" : "ACTIVE";
    setSavingStatus(true);
    try {
      await updatePromoStatus(nextStatus);
      await refresh();
      alertSuccess(nextStatus === "ACTIVE" ? "Code promo activé" : "Code promo désactivé");
    } catch {
      alertError("Erreur lors de la mise à jour du statut du code promo");
    } finally {
      setSavingStatus(false);
    }
  };

  const onUpdateReward = async (userId: string, amountParrain: number, amountFilleule: number) => {
    try {
      await updateRewardPerInvite(amountParrain, amountFilleule);
      await refresh();
      setShowUpdateRewardModal(false);
    } catch (error) {
      alertError("Une erreur s'est produite lors de la mise à jour des récompenses");
      throw error;
    }
  };

  if (loading) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
        <div className="bg-white rounded-lg shadow-xl w-full max-w-md mx-4 p-8">
          <div className="flex justify-center items-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-600"></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
        <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl mx-4 max-h-[90vh] overflow-y-auto">
          {/* Header */}
          <div className="sticky top-0 bg-white z-10 flex items-center justify-between px-6 py-4 border-b">
            <div className="flex items-center space-x-2">
              <Gift className="w-5 h-5 text-green-600" />
              <h2 className="text-xl font-semibold text-gray-900">Statistiques du code promo</h2>
            </div>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600 transition-colors">
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Body */}
          <div className="px-6 py-4">
            {error && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                <p className="text-sm text-red-600">{error}</p>
              </div>
            )}

            {promoCodeStat && promoCode && (
              <div className="space-y-4">
                {/* Code promo avec bouton modifier */}
                <div className="relative p-3 bg-gray-50 rounded-lg">
                  <div className="flex items-center justify-between">
                    <div className="flex-1 text-center">
                      <p className="text-sm text-gray-500">Code promo</p>
                      <p className="text-2xl font-bold text-green-600">{promoCode.user_promo_code}</p>
                      <span
                        className={`mt-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                          isPromoActive ? "bg-green-100 text-green-700" : "bg-gray-200 text-gray-600"
                        }`}
                      >
                        {isPromoActive ? "ACTIVE" : "INACTIVE"}
                      </span>
                    </div>
                    <div className="flex items-center space-x-1">
                      <button
                        onClick={handleTogglePromoStatus}
                        disabled={savingStatus}
                        className={`p-2 transition-colors ${
                          isPromoActive ? "text-gray-400 hover:text-red-600" : "text-gray-400 hover:text-green-600"
                        } disabled:opacity-50`}
                        title={isPromoActive ? "Désactiver le code promo" : "Activer le code promo"}
                      >
                        {savingStatus ? <Loader2 className="w-4 h-4 animate-spin" /> : <Power className="w-4 h-4" />}
                      </button>
                      <button
                        onClick={() => setShowUpdateCodeModal(true)}
                        className="p-2 text-gray-400 hover:text-blue-600 transition-colors"
                        title="Modifier le code promo"
                      >
                        <Edit2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </div>

                {/* Niveau et bouton modifier récompenses */}
                <div className="flex items-center justify-between p-3 bg-blue-50 rounded-lg">
                  <div>
                    <p className="text-sm text-gray-600">Niveau</p>
                    <p className="text-lg font-semibold text-blue-600">{promoCodeStat.level || "Starter"}</p>
                  </div>
                  <button
                    onClick={() => setShowUpdateRewardModal(true)}
                    className="flex items-center space-x-1 px-3 py-1.5 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                  >
                    <Edit2 className="w-3.5 h-3.5" />
                    <span>Modifier récompenses</span>
                  </button>
                </div>

                {/* Montants des récompenses */}
                <div className="p-3 bg-green-50 rounded-lg">
                  <p className="text-sm font-medium text-gray-700 mb-2">Récompenses par parrainage</p>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-xs text-gray-500">Pour le parrain</p>
                      <p className="text-xl font-bold text-green-600">
                        {(promoCodeStat.amount_received_parrain ?? promoCode.amount_received_parrain) || 0}{" "}
                        {displayCurrency}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500">Pour la filleule</p>
                      <p className="text-xl font-bold text-purple-600">
                        {(promoCodeStat.amount_received_filleule ?? promoCode.amount_received_filleule) || 0}{" "}
                        {displayCurrency}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Stats grid */}
                <div className="grid grid-cols-2 gap-3">
                  <div className="p-3 bg-purple-50 rounded-lg">
                    <div className="flex items-center space-x-2 mb-1">
                      <Users className="w-4 h-4 text-purple-600" />
                      <p className="text-sm text-gray-600">Total invités</p>
                    </div>
                    <p className="text-xl font-bold text-purple-600">{promoCodeStat.total_invited || 0}</p>
                  </div>

                  <div className="p-3 bg-yellow-50 rounded-lg">
                    <div className="flex items-center space-x-2 mb-1">
                      <Clock className="w-4 h-4 text-yellow-600" />
                      <p className="text-sm text-gray-600">En attente</p>
                    </div>
                    <p className="text-xl font-bold text-yellow-600">{promoCodeStat.pending || 0}</p>
                  </div>

                  <div className="p-3 bg-green-50 rounded-lg">
                    <div className="flex items-center space-x-2 mb-1">
                      <CheckCircle className="w-4 h-4 text-green-600" />
                      <p className="text-sm text-gray-600">Actifs</p>
                    </div>
                    <p className="text-xl font-bold text-green-600">{promoCodeStat.active || 0}</p>
                  </div>

                  <div className="p-3 bg-indigo-50 rounded-lg">
                    <div className="flex items-center space-x-2 mb-1">
                      <TrendingUp className="w-4 h-4 text-indigo-600" />
                      <p className="text-sm text-gray-600">Gains totaux</p>
                    </div>
                    <p className="text-xl font-bold text-indigo-600">
                      {promoCodeStat.earnings || 0} {displayCurrency}
                    </p>
                  </div>
                </div>

                {/* Montants détaillés */}
                <div className="border-t pt-4">
                  <p className="text-sm font-medium text-gray-700 mb-2">Détail des montants</p>
                  <div className="space-y-2">
                    <div className="flex justify-between">
                      <span className="text-gray-600">Montant gagné:</span>
                      <span className="font-semibold">
                        {promoCode.win_amount || 0} {displayCurrency}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Montant en attente:</span>
                      <span className="font-semibold text-yellow-600">
                        {promoCode.pending_amount || 0} {displayCurrency}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Montant transféré:</span>
                      <span className="font-semibold text-green-600">
                        {promoCode.transfered_amount || 0} {displayCurrency}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Share message */}
                <div className="border-t pt-4">
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-sm font-medium text-gray-700">Message de partage</p>
                    {!editingShareMessage && (
                      <button
                        onClick={() => {
                          setShareMessageDraft(promoCode?.share_message || "");
                          setEditingShareMessage(true);
                        }}
                        className="p-1.5 text-gray-400 hover:text-blue-600 transition-colors"
                        title="Modifier le message"
                      >
                        <Edit2 className="w-4 h-4" />
                      </button>
                    )}
                  </div>

                  {editingShareMessage ? (
                    <div className="space-y-2">
                      <textarea
                        value={shareMessageDraft}
                        onChange={(e) => setShareMessageDraft(e.target.value)}
                        rows={4}
                        className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 resize-y"
                        placeholder="Télécharge L'application IZI LIFE PAY..."
                      />
                      <div className="flex flex-wrap gap-2">
                        <span className="text-xs text-gray-500">Variables :</span>
                        <span className="px-1.5 py-0.5 text-xs font-mono bg-blue-50 text-blue-700 rounded">
                          {"{code}"}
                        </span>
                        <span className="px-1.5 py-0.5 text-xs font-mono bg-blue-50 text-blue-700 rounded">
                          {"{link}"}
                        </span>
                      </div>
                      <div className="flex gap-2">
                        <button
                          onClick={handleSaveShareMessage}
                          disabled={savingShareMessage}
                          className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-white bg-green-600 rounded-lg hover:bg-green-700 disabled:opacity-50"
                        >
                          {savingShareMessage ? (
                            <Loader2 className="w-3.5 h-3.5 animate-spin" />
                          ) : (
                            <Save className="w-3.5 h-3.5" />
                          )}
                          Sauvegarder
                        </button>
                        <button
                          onClick={() => setEditingShareMessage(false)}
                          className="px-3 py-1.5 text-sm text-gray-600 bg-gray-100 rounded-lg hover:bg-gray-200"
                        >
                          Annuler
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div className="p-3 bg-gray-50 rounded-lg whitespace-pre-wrap text-sm text-gray-600">
                      {promoCodeStat?.share_message || (
                        <span className="text-gray-400 italic">Aucun message personnalisé configuré</span>
                      )}
                    </div>
                  )}
                </div>

                {/* Section des referrals */}
                <div className="border-t pt-4">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center space-x-2">
                      <UserPlus className="w-5 h-5 text-green-600" />
                      <p className="text-md font-medium text-gray-700">Personnes parrainées</p>
                    </div>
                    <span className="text-sm text-gray-500">Total: {promoCodeStat.total_invited || 0}</span>
                  </div>
                  {referralList.length > 0 ? (
                    <div className="space-y-2 max-h-64 overflow-y-auto">
                      {referralList.map((referral: ReferralDetail, index: number) => (
                        <div
                          key={referral.user_id || index}
                          className="p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
                        >
                          <div className="flex items-center justify-between">
                            <div className="flex items-center space-x-3">
                              <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center">
                                <UserPlus className="w-4 h-4 text-green-600" />
                              </div>
                              <div>
                                <p className="font-medium text-gray-900">
                                  {referral.first_name && referral.last_name
                                    ? `${referral.first_name} ${referral.last_name}`
                                    : referral.email || `Utilisateur ${referral.user_id}`}
                                </p>

                                {referral.email && <p className="text-sm text-gray-500 mt-0.5">{referral.email}</p>}

                                <div className="flex items-center flex-wrap gap-2 mt-1">
                                  {referral.status && (
                                    <span
                                      className={`text-xs px-2 py-0.5 rounded-full ${
                                        referral.status === "ACTIVE"
                                          ? "bg-green-100 text-green-700"
                                          : "bg-yellow-100 text-yellow-700"
                                      }`}
                                    >
                                      {referral.status}
                                    </span>
                                  )}
                                  {referral.has_transferred ? (
                                    <span className="text-xs px-2 py-0.5 rounded-full bg-blue-100 text-blue-700">
                                      Transfert effectué
                                    </span>
                                  ) : (
                                    <span className="text-xs px-2 py-0.5 rounded-full bg-gray-100 text-gray-600">
                                      Aucun transfert
                                    </span>
                                  )}
                                  {referral.created_at && (
                                    <span className="text-xs text-gray-500 flex items-center">
                                      <Calendar className="w-3 h-3 mr-1" />
                                      {new Date(referral.created_at).toLocaleDateString()}
                                    </span>
                                  )}
                                </div>
                              </div>
                            </div>
                            <div className="text-right space-y-1">
                              {referral.has_transferred ? (
                                <div className="flex items-center justify-end space-x-1 text-sm font-semibold text-blue-700">
                                  <ArrowUpRight className="w-3.5 h-3.5" />
                                  <span>
                                    {(referral.transfer_total || 0).toLocaleString()}{" "}
                                    {getReferralTransferCurrency(referral)}
                                  </span>
                                </div>
                              ) : null}
                              {referral.earnings ? (
                                <p className="text-sm font-semibold text-green-600">
                                  +{referral.earnings} {getReferralEarningsCurrency(referral)}
                                </p>
                              ) : null}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center p-6 bg-gray-50 rounded-lg">
                      <UserPlus className="w-12 h-12 text-gray-300 mx-auto mb-2" />
                      <p className="text-gray-500">Aucune personne parrainée pour le moment</p>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="sticky bottom-0 bg-white px-6 py-4 border-t">
            <button
              onClick={onClose}
              className="w-full px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
            >
              Fermer
            </button>
          </div>
        </div>
      </div>

      {/* Modal modification code promo */}
      {showUpdateCodeModal && promoCode && (
        <UpdatePromoCodeModal
          user={user}
          currentCode={promoCode.user_promo_code}
          onClose={() => setShowUpdateCodeModal(false)}
          onUpdate={handleUpdatePromoCode}
        />
      )}

      {/* Modal modification récompense */}
      {showUpdateRewardModal && promoCode && (
        <UpdateRewardModal
          user={user}
          currentParrainAmount={Number(promoCode.amount_received_parrain) || 0}
          currentFilleuleAmount={Number(promoCode.amount_received_filleule) || 0}
          onClose={() => setShowUpdateRewardModal(false)}
          onUpdate={onUpdateReward}
        />
      )}
    </>
  );
}
